import * as React from 'react';
import { Box, Chip, CircularProgress, IconButton, Rating, Typography } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import dayjs from 'dayjs';

import { Popup } from 'components';
import { IZoneWithActions, IZone } from 'models';
import { EZoneStatus } from 'enums';
import { ReactComponent as ZoneIconCode } from 'assets/images/zone_icon_code.svg';
import { ReactComponent as ExternalLinkIcon } from 'assets/images/external_link_icon.svg';
import { getBuildingLocation } from 'utils';

export const convertZonesToTableData = (isAdmin: boolean) => (zones: Array<IZoneWithActions>) => {
  return zones.map((zone) => convertZoneToTableData(zone, isAdmin));
};

export const getZoneTableRowOptions = (zone: IZoneWithActions, isAdmin: boolean) => {
  const {
    viewCleancode = () => {},
    manageServiceItem = () => {},
    viewCleaningHistory = () => {},
    copyZone = () => {},
    editZone = () => {},
    deactivateZone = () => {},
    deleteZone = () => {},
    activateZone = () => {},
    downloadDecal = () => {},
  } = zone;

  let options: any[] = [];

  switch (zone.status) {
    case EZoneStatus.ACTIVE: {
      options = [
        {
          id: 1,
          label: 'View Cleancode',
          icon: <ExternalLinkIcon />,
          prependIcon: false,
          onClick: () => viewCleancode(zone),
          visible: () => true,
        },
        {
          id: 2,
          label: 'Manage service item',
          onClick: () => manageServiceItem(zone),
          visible: () => true,
        },
        {
          id: 3,
          label: 'View cleaning history',
          onClick: () => viewCleaningHistory(zone),
          visible: () => true,
        },
        {
          id: 4,
          label: 'Copy Zone',
          onClick: () => copyZone(zone),
          visible: () => true,
        },
        {
          id: 5,
          label: 'Edit Zone',
          onClick: () => editZone(zone),
          visible: () => true,
        },
        {
          id: 6,
          label: 'Download Decal',
          onClick: () => downloadDecal(zone),
          visible: () => true,
        },
        {
          id: 7,
          label: 'Deactivate Zone',
          onClick: () => deactivateZone(zone),
          visible: () => isAdmin,
        },
      ];

      break;
    }

    case EZoneStatus.INACTIVE: {
      options = [
        {
          id: 1,
          label: 'Activate Zone',
          onClick: () => activateZone(zone),
          visible: () => isAdmin,
        },
        {
          id: 2,
          label: 'Edit Zone',
          onClick: () => editZone(zone),
          visible: () => true,
        },
        {
          id: 3,
          label: 'Download Decal',
          onClick: () => downloadDecal(zone),
          visible: () => true,
        },
        {
          id: 4,
          label: 'Delete Zone',
          onClick: () => deleteZone(zone),
          visible: () => isAdmin,
        },
      ];

      break;
    }

    default:
  }

  return options;
};

export const convertZoneToTableData = (zone: IZoneWithActions, isAdmin: boolean) => {
  const options = getZoneTableRowOptions(zone, isAdmin);

  return {
    id: zone.id,
    created: dayjs(zone.created_at).format('MM/DD/YYYY'),
    code: (
      <Typography
        color="primary"
        variant="body2"
        onClick={() => (zone.editZone ? zone.editZone(zone) : null)}
        sx={{ cursor: 'pointer' }}
      >
        {zone.code}
      </Typography>
    ),
    name: zone.name,
    floorInfo: zone.floorInfo,
    rating: (
      <Rating
        value={zone.rating}
        readOnly
        size="small"
        sx={{
          marginLeft: 'auto',
        }}
      />
    ),
    serviceItemsText: (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          maxWidth: 240,
        }}
      >
        {(zone?.service_items || []).map((serviceItem) => (
          <Box
            key={`zone-service-item-${serviceItem.id}`}
            sx={{
              padding: 0.2,
            }}
          >
            <Chip
              label={serviceItem.name}
              size="small"
              sx={{
                borderRadius: 1,
                backgroundColor: '#FFA922',
                fontSize: 10,
              }}
            />
          </Box>
        ))}
      </Box>
    ),
    building: zone.building?.address,
    status: zone.status,
    lastServicedBy: zone.lastServicedBy,
    actions: (
      <Popup items={options.filter((o) => o.visible())} width={zone.status === EZoneStatus.ACTIVE ? 200 : 180}>
        <IconButton color="primary" size="small">
          <MoreHoriz />
        </IconButton>
      </Popup>
    ),
  };
};

type PriceInfo = {
  loading: boolean;
  basic: number;
};

export const convertZonesToOnboardingReviewTableData = (props: PriceInfo) => (zones: Array<any>) => {
  return zones.map((zone) => convertZoneToOnboardingReviewTableData(props)(zone));
};

export const convertZoneToOnboardingReviewTableData =
  ({ loading, basic }: PriceInfo) =>
  (zone: any) => {
    return {
      product: (
        <Typography color="primary" variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
          <ZoneIconCode />
          {zone?.code || ''}
        </Typography>
      ),
      name: zone?.name || '',
      price: loading ? (
        <CircularProgress color="primary" size={14} />
      ) : (
        <Typography color="primary" variant="body2">
          {`$${Number.parseFloat(`${basic}`).toFixed(2)}`}
        </Typography>
      ),
    };
  };

interface FilterBuildingItemsProps {
  searchKeyword: string;
  address: string;
  floor: string;
  name: string;
}

const isAddress = (zone: IZone, keyword: string) =>
  zone.building && getBuildingLocation(zone.building).toLowerCase().includes(keyword.toLowerCase());
const isFloors = (zone: IZone, keyword: string) => zone.floorInfo.toLowerCase().includes(keyword.toLowerCase());
const isName = (zone: IZone, keyword: string) => (zone.name ?? '').toLowerCase().includes(keyword.toLowerCase());

const isFiltered = (zone: IZone, filters: FilterBuildingItemsProps) => {
  const matchesSearch =
    !filters.searchKeyword ||
    isAddress(zone, filters.searchKeyword) ||
    isFloors(zone, filters.searchKeyword) ||
    isName(zone, filters.searchKeyword);

  const matchesAddress = !filters.address || (zone.building && getBuildingLocation(zone.building)) === filters.address;
  const matchesFloor = !filters.floor || (zone?.floorInfo ?? '') === filters.floor;
  const matchesName = !filters.name || isName(zone, filters.name);

  return matchesSearch && matchesAddress && matchesFloor && matchesName;
};

export const filterZoneItems = (
  zones?: Array<IZone>,
  filters: FilterBuildingItemsProps = {
    searchKeyword: '',
    address: '',
    floor: '',
    name: '',
  },
) => {
  if (!zones || zones.length === 0) {
    return [];
  }

  const hasActiveFilter = filters.searchKeyword || filters.address || filters.floor || filters.name;

  if (!hasActiveFilter) return zones;

  return zones.filter((zone) => isFiltered(zone, filters));
};
